import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  BUTTON_STYLES,
  FORM_STYLES,
  MODAL_STYLES,
  TEXT_FIELD_STYLES,
} from "../../styles/componentStyles";
import { useFormik } from "formik";
import api from "../../axiosInstance";
import { toast } from "react-toastify";
import faqValidationSchema from "../Validations/BannerValidation";

// universities: the list shown in the University field. defaultUniversityId:
// the university picked in the FAQ table's filter ("" = all), used for a new FAQ.
function FaqModal({
  States,
  open,
  onClose,
  faqEdit,
  onSuccess,
  universities = [],
  defaultUniversityId = "",
}) {
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // Remove the Dubulicate categories
  const uniqueByCategory = categories.filter(
    (value, index, self) =>
      index === self.findIndex((obj) => obj.category === value.category)
  );

  //  Form vlaidation using useFormik hook
  const formik = useFormik({
    initialValues: {
      category: faqEdit?.category || "",
      question: faqEdit?.question || "",
      answer: faqEdit?.answer || "",
      page: faqEdit?.page || "",
      displayOrder: faqEdit?.displayOrder || "",
      active: faqEdit?.active ?? "true",
      // "" = global FAQ (every university). An edit keeps the FAQ's own
      // university; a new FAQ starts on the table's selected university.
      universityId: faqEdit?.id
        ? faqEdit?.universityId ?? ""
        : defaultUniversityId ?? "",
    },

    validationSchema: faqValidationSchema.faqValidationSchema,
    enableReinitialize: true,

    onSubmit: async (values, { resetForm }) => {
      console.log(faqEdit?.id);
      try {
        let response;
        // universityId is always sent, so an edit can also move an FAQ back to
        // global (null).
        const payload = {
          ...values,
          category: values.category.trim(),
          universityId:
            values.universityId === "" ? null : Number(values.universityId),
        };
        // If it's Edit data it will call Update api
        if (faqEdit?.id) {
          response = await api.put(`/api/v1/faqs/${faqEdit?.id}`, payload);
        }

        // Else it's Add data it will call Add api
        else {
          response = await api.post("/api/v1/faqs", payload);
        }
        // Show success toast
        toast.success(
          `FAQ ${faqEdit?.id ? "updated" : "added"} successfully!`,
          {
            position: "top-right",
            autoClose: 2000,
          }
        );
        onSuccess(response.data.data);
        resetForm();
        onClose();
      } catch (error) {
        toast.error("Failed to submit. Please try again.", {
          autoClose: 3000,
        });
        console.error("API Error:", error);
      }
    },
  });
  // Categories Fetching api
  useEffect(() => {
    api
      .get("/api/v1/faqs")
      .then((res) => {
        setCategories(res.data.data);
      })
      .catch((error) => {
        console.log(error);
        setError("Failed to load Categories list. Please try again.");
      })
      .finally(setIsLoading(false));
  }, []);
  return (
    <>
      {/* {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )} */}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      <Modal open={open} onClose={onClose} disableScrollLock>
        <Box
          sx={{ ...MODAL_STYLES.standard, width: { xs: 340, md: 500 } }}
          component="form"
          onSubmit={formik.handleSubmit}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{ mb: 3, fontWeight: 600 }}
          >
            {faqEdit && Object.keys(faqEdit).length > 0
              ? "Update FAQ"
              : "Add FAQ"}
          </Typography>
          <Box sx={FORM_STYLES.container}>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="faq-university-label">University</InputLabel>
              <Select
                labelId="faq-university-label"
                id="universityId"
                name="universityId"
                value={formik.values.universityId}
                onChange={formik.handleChange}
                label="University"
              >
                <MenuItem value="">All universities (global FAQ)</MenuItem>
                {universities.map((university) => (
                  <MenuItem value={university.id} key={university.id}>
                    {university.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                A global FAQ is shown to students of every university.
              </FormHelperText>
            </FormControl>
            {/* Pick an existing category or type a new one. */}
            <Autocomplete
              freeSolo
              options={uniqueByCategory
                .map((faq) => faq.category)
                .filter(Boolean)}
              inputValue={formik.values.category}
              onInputChange={(event, value) =>
                formik.setFieldValue("category", value ?? "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  name="category"
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.category && Boolean(formik.errors.category)
                  }
                  helperText={
                    (formik.touched.category && formik.errors.category) ||
                    "Choose a category or type a new one"
                  }
                  required
                />
              )}
              sx={{ mt: 2 }}
            />
            <TextField
              label="Question"
              name="question"
              value={formik.values.question}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.question && Boolean(formik.errors.question)}
              helperText={formik.touched.question && formik.errors.question}
              {...TEXT_FIELD_STYLES.standard}
              required
            />
            <TextField
              label="Answer"
              multiline
              rows={2}
              name="answer"
              value={formik.values.answer}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.answer && Boolean(formik.errors.answer)}
              helperText={formik.touched.answer && formik.errors.answer}
              {...TEXT_FIELD_STYLES.standard}
            />
            <FormControl
              fullWidth
              error={formik.touched.page && Boolean(formik.errors.page)}
              sx={{ mt: 2 }}
            >
              <InputLabel id="active-label">Page</InputLabel>
              <Select
                labelId="active-label"
                id="page"
                name="page"
                value={formik.values.page}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="page"
              >
                <MenuItem value="coursepage">Course page</MenuItem>
                <MenuItem value="subscriptionpage">Subscription Page</MenuItem>
              </Select>
              {formik.touched.page && formik.errors.page && (
                <FormHelperText>{formik.errors.page}</FormHelperText>
              )}
            </FormControl>
            <TextField
              label="DisplayOrder"
              name="displayOrder"
              type="number"
              inputProps={{ min: 0 }}
              value={formik.values.displayOrder}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.displayOrder &&
                Boolean(formik.errors.displayOrder)
              }
              helperText={
                formik.touched.displayOrder && formik.errors.displayOrder
              }
              {...TEXT_FIELD_STYLES.standard}
            />
            <FormControl
              fullWidth
              error={formik.touched.active && Boolean(formik.errors.active)}
              sx={{ mt: 2 }}
            >
              <InputLabel id="active-label">Active</InputLabel>
              <Select
                labelId="active-label"
                id="active"
                name="active"
                value={formik.values.active}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Active"
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
              {formik.touched.active && formik.errors.active && (
                <FormHelperText>{formik.errors.active}</FormHelperText>
              )}
            </FormControl>
            <Box
              sx={FORM_STYLES.actions}
              style={{
                display: "flex",
                marginTop: "20px",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <Button onClick={onClose} {...BUTTON_STYLES.secondary}>
                Cancel
              </Button>
              <Button type="submit" {...BUTTON_STYLES.primary}>
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default FaqModal;
